/**
 * ConfidenceBadge Component
 *
 * Visual indicator for confidence scores on extracted evidence.
 * Displays color-coded badges for low, medium, and high confidence levels.
 *
 * Requirements: 4.3, 6.3, 13.5
 */

import React from 'react';
import { ConfidenceLevel } from '../../types/extraction';
import styles from './ConfidenceBadge.module.css';

export interface ConfidenceBadgeProps {
  /** The confidence level to display */
  level: ConfidenceLevel;
  /** Whether to show the text label alongside the badge */
  showLabel?: boolean;
}

/**
 * Maps confidence levels to display labels
 */
const CONFIDENCE_LABELS: Record<ConfidenceLevel, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Maps confidence levels to accessible descriptions
 */
const CONFIDENCE_DESCRIPTIONS: Record<ConfidenceLevel, string> = {
  high: 'High confidence - extraction is reliable',
  medium: 'Medium confidence - review recommended',
  low: 'Low confidence - manual verification required',
};

/**
 * ConfidenceBadge displays a color-coded indicator for extraction confidence.
 *
 * Color coding:
 * - High: Green (#107c10) - indicates reliable extraction
 * - Medium: Amber (#ff8c00) - indicates review recommended
 * - Low: Red (#d13438) - indicates manual verification required
 *
 * @example
 * // Badge with label
 * <ConfidenceBadge level="high" showLabel />
 *
 * @example
 * // Badge without label (icon only)
 * <ConfidenceBadge level="low" />
 */
export const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  level,
  showLabel = false,
}) => {
  const label = CONFIDENCE_LABELS[level];
  const description = CONFIDENCE_DESCRIPTIONS[level];

  // Determine the CSS class based on confidence level
  const getBadgeClass = (): string => {
    switch (level) {
      case 'high':
        return styles.high;
      case 'medium':
        return styles.medium;
      case 'low':
        return styles.low;
      default:
        return styles.medium;
    }
  };

  return (
    <span
      className={`${styles.badge} ${getBadgeClass()}`}
      role="status"
      aria-label={description}
      title={description}
    >
      <span className={styles.indicator} aria-hidden="true" />
      {showLabel && <span className={styles.label}>{label}</span>}
    </span>
  );
};

export default ConfidenceBadge;
